import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { useStartWorkflow } from "../hooks/use-start-workflow.hook";
import { useCaseResolution } from "../hooks/use-workflow-board.hook";

/**
 * Lead-page banner for "Nawy Now" leads that have no ACTIVE workflow case yet (mock: orange
 * "Next action — Send iscore to customer" + a Start flow button above the tab bar).
 * Starting the flow goes through crm-backend, which sources the customer itself and creates
 * the workflow-manager case WITH its PRIMARY participant; the create invalidates the
 * nawy-now query tree, the case check refetches, and the banner hides itself.
 */
const NawyNowLeadBanner = ({
  leadUid,
  journeyUid,
  customerUId,
}: {
  leadUid?: string;
  journeyUid?: string;
  customerUId?: string;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  const { create } = useStartWorkflow();

  // §NetAudit (2026-08-05): this used to be a private ["nawy-now","lead-cases"] query — the same
  // GET /v1/cases the board's case resolution already makes, fetched a second time on every load
  // and on every ["nawy-now"] invalidation. The shared resolution answers the only question asked
  // here: it lists the journey's cases, and any case at all suppresses the banner — a COMPLETED (or
  // rejected) journey is finished, not waiting for a "Start flow" (gating on ACTIVE-only resurrected the
  // CTA after completion). §Multi-application: a SECOND application starts from the tab's header instead.
  const resolutionQuery = useCaseResolution(journeyUid);
  const hasAnyCase = (resolutionQuery.data?.length ?? 0) > 0;
  // render nothing until the check settles — a banner that flashes in and out is worse than a late
  // one (keepPreviousData also keeps a lead-switch from replaying the previous lead's answer as
  // undefined; a stale non-empty list just keeps the banner hidden until the refetch lands)
  if (!leadUid || !journeyUid || resolutionQuery.data === undefined || hasAnyCase)
    return null;

  // No client-side validation — the button always fires; backend errors surface as toasts.
  const handleStart = () =>
    create.mutate({ customerUid: customerUId, leadUid });

  // Figma "CRM Web — Delivery" node 11544:347365: the warning ramp there (#FFF6F0 /
  // #F76700 / #E65F00) differs from the theme's orange primitives, so those three are
  // taken verbatim from the design variables (layer/warning-light, border/warning,
  // layer/warning + text/warning).
  return (
    <Box
      sx={{
        width: "100%",
        px: 2,
        // §MobileQA: the design nests this alert in the white Header block at p-16 — the top inset
        // was 12. (The alert-to-name gap is still 12 + the profile block's own 16; closing that to
        // the design's 12 belongs to the lead-page layout, not here.)
        pt: 2,
        pb: 1.5,
        backgroundColor: colors.semantic.layer.primary,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 2,
          py: 1.5,
          borderRadius: theme.nawy.radius.xl,
          border: "0.5px solid #F76700",
          backgroundColor: "#FFF6F0",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            fontSize={11}
            lineHeight="16px"
            color={colors.semantic.text.secondary}
          >
            Next action
          </Typography>
          <Typography
            fontSize={14}
            lineHeight="20px"
            fontWeight={600}
            color="#E65F00"
            noWrap
          >
            Send iscore to customer
          </Typography>
        </Box>

        <Button
          variant="contained"
          disableElevation
          disabled={create.isLoading}
          onClick={handleStart}
          startIcon={
            create.isLoading ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined
          }
          sx={{
            textTransform: "none",
            whiteSpace: "nowrap",
            height: 32,
            px: 2,
            fontSize: 14,
            fontWeight: 600,
            lineHeight: "20px",
            color: colors.semantic.text.onColor,
            borderRadius: theme.nawy.radius.lg,
            backgroundColor: "#E65F00",
            "&:hover": { backgroundColor: "#CC5500" },
          }}
        >
          Start flow
        </Button>
      </Box>
    </Box>
  );
};

export default NawyNowLeadBanner;
